import React, { useEffect, useState, createElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { StatusTimeline } from '../components/tracking/StatusTimeline';
import { useOrderTracking } from '../lib/hooks';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { formatCurrency, getStatusColor, getStatusLabel } from '../lib/utils';
import { Search, MapPin, Store, Receipt, Phone, Copy, CheckCircle2, Car, Clock, AlertTriangle } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { useLanguage } from '../lib/i18n';
export function TrackingPage() {
  const {
    trackingToken
  } = useParams<{
    trackingToken: string;
  }>();
  const {
    order,
    events,
    deliveryJob,
    driverInfo,
    loading,
    error
  } = useOrderTracking(trackingToken);
  const [copied, setCopied] = useState(false);
  const [searchingMinutes, setSearchingMinutes] = useState(0);
  const {
    t
  } = useLanguage();
  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(window.location.href);
      } else {
        // Safari / WhatsApp fallback using execCommand
        const textArea = document.createElement('textarea');
        textArea.value = window.location.href;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail if clipboard is blocked
      setCopied(false);
    }
  };
  // Calculate searching time
  useEffect(() => {
    if (order?.status === 'searching_driver' && deliveryJob?.created_at) {
      const interval = setInterval(() => {
        const diff = Math.floor((new Date().getTime() - new Date(deliveryJob.created_at).getTime()) / 60000);
        setSearchingMinutes(diff > 0 ? diff : 0);
      }, 10000);
      // Initial calculation
      const diff = Math.floor((new Date().getTime() - new Date(deliveryJob.created_at).getTime()) / 60000);
      setSearchingMinutes(diff > 0 ? diff : 0);
      return () => clearInterval(interval);
    }
  }, [order?.status, deliveryJob?.created_at]);
  if (loading) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-5697">
        <MarketplaceHeader data-id="element-5698" />
        <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-12" data-id="element-5699">
          <Skeleton className="h-12 w-1/2 mb-8" data-id="element-5700" />
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-8" data-id="element-5701">
            <Skeleton className="h-8 w-1/3 mb-4" data-id="element-5702" />
            <Skeleton className="h-4 w-full mb-2" data-id="element-5703" />
            <Skeleton className="h-4 w-2/3" data-id="element-5704" />
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-5705">
            {[1, 2, 3, 4].map(i => <div key={i} className="flex gap-4 mb-6" data-id="element-5706">
                <Skeleton className="h-10 w-10 rounded-full shrink-0" data-id="element-5707" />
                <div className="flex-1" data-id="element-5708">
                  <Skeleton className="h-6 w-1/3 mb-2" data-id="element-5709" />
                  <Skeleton className="h-4 w-full" data-id="element-5710" />
                </div>
              </div>)}
          </div>
        </main>
        <Footer data-id="element-5711" />
      </div>;
  }
  if (error || !order) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-5712">
        <MarketplaceHeader data-id="element-5713" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-5714">
          <EmptyState icon={Search} title={t('tracking_not_found')} description={t('error')} actionLabel={t('confirm_back')} onAction={() => window.location.href = '/'} data-id="element-5715" />
        </main>
        <Footer data-id="element-5716" />
      </div>;
  }
  const trackingUrl = window.location.href;
  const statusLabel = t(`status_${order.status}`) || getStatusLabel(order.status);
  return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-5717">
      <MarketplaceHeader data-id="element-5718" />

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-8 sm:py-12" data-id="element-5719">
        <div className="flex items-center justify-between mb-8" data-id="element-5720">
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-5721">
            {t('tracking_title')}
          </h1>
          <Badge variant="secondary" className={`text-sm px-3 py-1 ${getStatusColor(order.status)}`} data-id="element-5722">
            {statusLabel}
          </Badge>
        </div>

        {/* Order Info Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8" data-id="element-5723">
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-100" data-id="element-5724">
            <div data-id="element-5725">
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-5726">
                {t('confirm_order_number')}
              </p>
              <p className="text-2xl font-black text-gray-900" data-id="element-5727">
                #{order.order_number}
              </p>
            </div>
            <div className="text-right" data-id="element-5728">
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-5729">
                {t('total')}
              </p>
              <p className="text-2xl font-black text-brand-primary" data-id="element-5730">
                {formatCurrency(order.total)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-5731">
            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4" data-id="element-5732">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0" data-id="element-5733">
                <Store className="w-5 h-5 text-blue-600" data-id="element-5734" />
              </div>
              <div data-id="element-5735">
                <h3 className="text-sm font-semibold text-gray-500 mb-1" data-id="element-5736">
                  Restaurante
                </h3>
                <p className="font-medium text-gray-900" data-id="element-5737">
                  Pedido del restaurante
                </p>
                <p className="text-sm text-gray-500 mt-1" data-id="element-5738">
                  {order.delivery_type === 'pickup' ? 'Para llevar' : order.delivery_type === 'delivery' ? 'Delivery' : 'Comer aquí'}
                </p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4" data-id="element-5739">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center shrink-0" data-id="element-5740">
                <Receipt className="w-5 h-5 text-emerald-600" data-id="element-5741" />
              </div>
              <div data-id="element-5742">
                <h3 className="text-sm font-semibold text-gray-500 mb-1" data-id="element-5743">
                  {t('total')}
                </h3>
                <p className="font-bold text-xl text-gray-900" data-id="element-5744">
                  {formatCurrency(order.total)}
                </p>
                <p className="text-sm text-gray-500 mt-1" data-id="element-5745">
                  Pago:{' '}
                  {order.payment_method === 'sinpe' ? 'SINPE Móvil' : order.payment_method === 'cash' ? 'Efectivo' : 'Tarjeta'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Driver Status Card (Only for delivery orders in specific states) */}
        {order.delivery_type === 'delivery' && <>
            {order.status === 'searching_driver' && <div className="bg-orange-50 rounded-2xl border border-orange-200 shadow-sm p-6 mb-8 flex items-center gap-4" data-id="element-5746">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center shrink-0 animate-pulse" data-id="element-5747">
                  <Search className="w-6 h-6 text-orange-600" data-id="element-5748" />
                </div>
                <div data-id="element-5749">
                  <h3 className="font-bold text-orange-900 text-lg" data-id="element-5750">
                    Buscando un conductor para tu pedido...
                  </h3>
                  <p className="text-orange-700 font-medium flex items-center gap-1.5 mt-1" data-id="element-5751">
                    <Clock className="w-4 h-4" data-id="element-5752" />
                    Lleva {searchingMinutes}{' '}
                    {searchingMinutes === 1 ? 'minuto' : 'minutos'} buscando
                  </p>
                </div>
              </div>}

            {driverInfo && (order.status === 'ready' || order.status === 'out_for_delivery') && <div className="bg-cyan-50 rounded-2xl border border-cyan-200 shadow-sm p-6 mb-8 flex items-center gap-4" data-id="element-5753">
                  <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center shrink-0" data-id="element-5754">
                    <Car className="w-6 h-6 text-cyan-600" data-id="element-5755" />
                  </div>
                  <div className="flex-1" data-id="element-5756">
                    <h3 className="font-bold text-cyan-900 text-lg" data-id="element-5757">
                      {order.status === 'out_for_delivery' ? 'Tu pedido está en camino' : 'Tu conductor está en camino al restaurante'}
                    </h3>
                    <div className="mt-2 bg-white/60 rounded-xl p-3 border border-cyan-100" data-id="element-5758">
                      <p className="font-bold text-gray-900" data-id="element-5759">
                        {driverInfo.name}
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-5760">
                        {driverInfo.vehicleType} • {driverInfo.plate}
                      </p>
                    </div>
                  </div>
                </div>}
          </>}

        {/* Main Timeline */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 mb-8" data-id="element-5761">
          <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-5762">
            {t('tracking_title')}
          </h2>
          <StatusTimeline currentStatus={order.status} events={events} restaurantId={order.restaurant_id} deliveryType={order.delivery_type} data-id="element-5763" />
        </div>

        {/* Delivery Info if applicable */}
        {order.delivery_type === 'delivery' && order.delivery_address && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8" data-id="element-5764">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-5765">
              <MapPin className="w-5 h-5 text-gray-400" data-id="element-5766" />
              {t('checkout_address')}
            </h3>
            <p className="text-gray-600" data-id="element-5767">{order.delivery_address}</p>
            {order.notes && <div className="mt-4 p-3 bg-amber-50 rounded-xl text-sm text-amber-800 border border-amber-100" data-id="element-5768">
                <span className="font-semibold" data-id="element-5769">Notas:</span> {order.notes}
              </div>}
          </div>}

        {/* Tracking Link */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-5770">
          <p className="text-sm font-medium text-gray-900 mb-4" data-id="element-5771">
            {t('copy_tracking')}
          </p>
          <div className="flex items-center gap-3" data-id="element-5772">
            <div className="flex-1 bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-500 truncate border border-gray-100" data-id="element-5773">
              {trackingUrl}
            </div>
            <button onClick={handleCopyLink} className="shrink-0 flex items-center justify-center w-12 h-12 rounded-xl border-2 border-gray-200 hover:border-brand-primary hover:text-brand-primary transition-colors" data-id="element-5774">
              {copied ? <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-5775" /> : <Copy className="w-5 h-5" data-id="element-5776" />}
            </button>
          </div>
        </div>
      </main>

      <Footer data-id="element-5777" />
    </div>;
}